import { useState } from "react";
import { toast } from "sonner";
import { ArrowDownToLine, MonitorDown, UserPlus, BadgeCheck, Search, ExternalLink, FileArchive } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { SmartScreenDialog } from "./SmartScreenGuide";
import { API } from "@/lib/api";

const STEPS = [
  { icon: MonitorDown, title: "Download & Install", text: "Get the desktop portal below and run the setup — installation takes under a minute." },
  { icon: UserPlus, title: "Create Your Profile", text: "Register your carrier profile directly in the portal with your authority and insurance details." },
  { icon: BadgeCheck, title: "Get Verified", text: "Our team verifies your credentials within minutes — no paperwork, no waiting queues." },
  { icon: Search, title: "Search Loads", text: "Browse available loads across dry van, reefer and flatbed and commit to the lanes you want." },
];

export default function Portal({ settings }) {
  const portal = settings?.portal;
  const [showGuide, setShowGuide] = useState(false);

  const handleDownload = () => {
    if (!portal || (!portal.type)) {
      toast.info("The portal setup is being finalized. Contact dispatch for early access.");
      return;
    }
    if (portal.type === "file") {
      toast.success("Downloading portal setup...");
      window.location.href = `${API}/portal/download`;
      setShowGuide(true);
    } else {
      toast.success("Opening the portal download page...");
      window.open(portal.url, "_blank", "noopener");
      setShowGuide(true);
    }
  };

  const sizeLabel = portal?.size ? ` · ${(portal.size / (1024 * 1024)).toFixed(1)} MB` : "";

  return (
    <section id="onboarding" className="relative overflow-hidden bg-[#0B172A] py-24 lg:py-32">
      <div className="pointer-events-none absolute right-0 top-1/3 h-[400px] w-[500px] rounded-full bg-[#0066FF]/15 blur-[130px]" />

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Carrier Onboarding</Eyebrow>
            <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
              One portal. Every load. Verified within minutes.
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-400">
              Download the PORTBRIDGE Carrier Portal to complete onboarding and start searching
              available loads the same day. Verification is automated, guided and typically finished
              in minutes — not days.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button
                data-testid="portal-download-exe-button"
                onClick={handleDownload}
                className="group flex items-center gap-2.5 rounded-full bg-[#0066FF] px-7 py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#0052cc] hover:shadow-[0_0_40px_rgba(0,102,255,0.55)]"
              >
                <ArrowDownToLine className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
                Download Portal Setup
              </button>
              {portal?.type && (
                <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-slate-500">
                  <FileArchive className="h-4 w-4 text-[#00F0FF]" />
                  {portal.type === "file" ? `${portal.filename}${sizeLabel}` : "Official download link attached"}
                </span>
              )}
            </div>
          </Reveal>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {STEPS.map((step, i) => (
              <Reveal key={step.title} delay={0.06 + i * 0.07} className={i % 2 === 1 ? "sm:mt-8" : ""}>
                <div className="h-full rounded-2xl border border-[#0066FF]/20 bg-[#070F1E] p-6 transition-colors hover:border-[#00F0FF]/50">
                  <div className="flex items-center justify-between">
                    <step.icon className="h-6 w-6 text-[#00F0FF]" />
                    <span className="font-mono text-xs font-bold text-[#0066FF]/60">0{i + 1}</span>
                  </div>
                  <h3 className="mt-4 font-display text-base font-bold text-white">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      <SmartScreenDialog open={showGuide} onOpenChange={setShowGuide} />
    </section>
  );
}
